import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import TriangleDown from "../../assets/icons/triangle-down.svg";

import config from "../../config";
import Button from "../../components/button";
import Hero from "../../components/hero";

import styles from "./locations.module.scss";

import useViewport, { Viewport } from "../../hooks/useViewport";
import SeoMeta from "../../components/seo-meta";

const MapMarkers = {
  INITIAL: "/images/map-marker.svg",
  HIGHLIGHT: "/images/map-marker-highlight.svg"
};

const LocationListItem = ({ location, onMouseEnter, onMouseLeave }) => {
  const device = useViewport();
  return (
    <Link href={`/locations/${location.slug}`} passHref>
      <a onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <div className={styles.LocationListItem}>
          <div className={styles.ImageWrapper}>
            {location.featuredImage && (
              <Image
                src={location.featuredImage?.url}
                alt={location.name}
                layout="fill"
                objectFit="cover"
              />
            )}
          </div>
          <div className={styles.LocationInfo}>
            <div>
              <h2 className={styles.LocationName}>{location.name}</h2>
              <div className={styles.LocationCity}>{location.city}</div>
            </div>

            <div className={styles.LocationAmenities}>
              {device === Viewport.PHONE &&
                location.amenitiesShort.split(", ").join(" - ")}
              {device !== Viewport.PHONE && location.amenitiesShort}
            </div>
          </div>
        </div>

        <Link href={`/locations/${location.slug}`} passHref>
          <Button rounded outline small className={styles.LocationView}>
            View Location
          </Button>
        </Link>
      </a>
    </Link>
  );
};

export default function LocationsPage({ locations, seo }) {
  const [locationHighlightIndex, setLocationHighlightIndex] = useState(null);

  return (
    <>
      <div className="container">
        <SeoMeta {...seo} />
        <Hero
          title="Locations"
          description="Discover a location that works for you"
        />
        <div className={styles.LocationsWrapper}>
          <div className={styles.LocationsList}>
            {locations.map((location, index) => (
              <LocationListItem
                key={location.name}
                location={location}
                onMouseEnter={() => setLocationHighlightIndex(index)}
                onMouseLeave={() => setLocationHighlightIndex(null)}
              />
            ))}
          </div>
          <div className={styles.LocationsMap}>
            <div className={styles.Map}>
              <Map
                initialViewState={{
                  bounds: locations.map((l) => [l.longitude, l.latitude]),
                  fitBoundsOptions: {
                    maxZoom: 12.85
                  }
                }}
                attributionControl={false}
                mapStyle={config.MAPBOX_STYLE}
                mapboxAccessToken={config.MAPBOX_TOKEN}
                onClick={() => setLocationHighlightIndex(null)}
              >
                {locations.map((location, index) => (
                  <Marker
                    key={location.slug}
                    longitude={location.longitude}
                    latitude={location.latitude}
                    anchor="bottom"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setLocationHighlightIndex(index);
                    }}
                    mapStyle={{}}
                  >
                    {locationHighlightIndex === index ? (
                      <div style={{ position: "relative" }}>
                        <div className={styles.MapInfoBox}>
                          <div className={styles.InfoImageWrapper}>
                            <Image
                              src={location.featuredImage.url}
                              alt=""
                              width="225"
                              height="125"
                              layout="responsive"
                              objectFit="cover"
                            />
                          </div>
                          <div className={styles.InfoBoxContent}>
                            <h3>{location.name}</h3>
                            <p>{location.city}</p>
                          </div>
                          <TriangleDown
                            className={styles.Triangle}
                            width="25"
                            height="25"
                            role="presentation"
                          />
                        </div>
                        <img
                          alt=""
                          src={
                            locationHighlightIndex === index
                              ? MapMarkers.HIGHLIGHT
                              : MapMarkers.INITIAL
                          }
                          width="25"
                          height="25"
                        />
                      </div>
                    ) : (
                      <img
                        alt=""
                        src={
                          locationHighlightIndex === index
                            ? MapMarkers.HIGHLIGHT
                            : MapMarkers.INITIAL
                        }
                        width="25"
                        height="25"
                      />
                    )}
                  </Marker>
                ))}
              </Map>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getServerSideProps() {
  try {
    const { getLocations } = await import("../../lib/locations");
    const { getSEOPageProps } = await import("../../lib/seo");
    
    const [locations, seo] = await Promise.all([
      getLocations(),
      getSEOPageProps({ resolvedUrl: "/locations" })
    ]);

    return {
      props: {
        locations,
        seo
      },
    };
  } catch (e) {
    console.log(e.message);
  }
}
